import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, Eye, AlertTriangle, Ban, CheckCircle, MessageSquare } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminViolations() {
  const [violations, setViolations] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => { fetchData(); }, [filter, page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [vRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/violations?status=${filter}&page=${page}`, { headers }),
        axios.get(`${API}/admin/violations/stats`, { headers })
      ]);
      setViolations(vRes.data.violations);
      setTotalPages(vRes.data.pages);
      setStats(sRes.data);
    } catch (err) { toast.error('Veriler yuklenemedi'); }
    finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/violations/${id}`, { action }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'suspended' ? 'Magaza askiya alindi' : 'Ihlal guncellendi');
      fetchData();
    } catch (err) { toast.error('Islem basarisiz'); }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    reviewed: 'bg-blue-100 text-blue-800',
    warned: 'bg-orange-100 text-orange-800',
    suspended: 'bg-red-100 text-red-800'
  };

  const violationTypeLabels = {
    telefon_numarasi: 'Telefon',
    email_adresi: 'Email',
    sosyal_medya: 'Sosyal Medya',
    adres_bilgisi: 'Adres'
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-red-500" /> Satici Ihlalleri
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Bekleyen', value: stats.pending || 0, color: 'bg-yellow-50 border-yellow-200 text-yellow-700', key: 'pending' },
          { label: 'Incelendi', value: stats.reviewed || 0, color: 'bg-blue-50 border-blue-200 text-blue-700', key: 'reviewed' },
          { label: 'Uyari', value: stats.warned || 0, color: 'bg-orange-50 border-orange-200 text-orange-700', key: 'warned' },
          { label: 'Askiya Alindi', value: stats.suspended || 0, color: 'bg-red-50 border-red-200 text-red-700', key: 'suspended' },
          { label: 'Toplam', value: stats.total || 0, color: 'bg-slate-50 border-slate-200 text-slate-700', key: 'all' },
        ].map(s => (
          <button key={s.key} onClick={() => { setFilter(s.key); setPage(1); }}
            className={`p-3 rounded-xl border text-center transition-all ${s.color} ${filter === s.key ? 'ring-2 ring-offset-1 ring-orange-400' : ''}`}
            data-testid={`filter-${s.key}`}
          >
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Violations List */}
      {loading ? (
        <div className="text-center py-10"><div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto" /></div>
      ) : violations.length === 0 ? (
        <div className="text-center py-10 text-slate-500">Bu kategoride ihlal bulunamadi</div>
      ) : (
        <div className="space-y-3">
          {violations.map(v => (
            <div key={v.id} className="bg-white rounded-xl border p-4" data-testid={`violation-${v.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-slate-900">{v.store_name}</span>
                    <Badge className={statusColors[v.status] || 'bg-slate-100'}>{v.status}</Badge>
                    <span className="text-xs text-slate-400">{v.source === 'seller_message' ? 'Satici Mesaji' : 'Musteri Mesaji'}</span>
                  </div>
                  <p className="text-xs text-slate-500">Kullanici: {v.user_name} | {new Date(v.created_at).toLocaleString('tr-TR')}</p>
                </div>
                <div className="flex gap-1">
                  {v.violation_types?.map(t => (
                    <Badge key={t} variant="destructive" className="text-xs">
                      {violationTypeLabels[t] || t}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Message Content */}
              <div className="bg-red-50 rounded-lg p-3 mb-3 border border-red-100">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-800 break-all">{v.message}</p>
                </div>
              </div>

              {/* Actions */}
              {v.status === 'pending' && (
                <div className="flex gap-2">
                  <button onClick={() => handleAction(v.id, 'reviewed')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs hover:bg-blue-600">
                    <Eye className="h-3 w-3" /> Incelendi
                  </button>
                  <button onClick={() => handleAction(v.id, 'warned')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs hover:bg-orange-600">
                    <AlertTriangle className="h-3 w-3" /> Uyar
                  </button>
                  <button onClick={() => handleAction(v.id, 'suspended')}
                    className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600">
                    <Ban className="h-3 w-3" /> Askiya Al
                  </button>
                </div>
              )}
              {v.admin_note && <p className="text-xs text-slate-500 mt-2">Admin notu: {v.admin_note}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              className={`w-8 h-8 rounded-lg text-sm ${p === page ? 'bg-orange-500 text-white' : 'bg-white border text-slate-600'}`}
            >{p}</button>
          ))}
        </div>
      )}
    </div>
  );
}
